import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { IRegistrant } from "../../types/registrant";
import { IProjectAccess } from "../../types/user";
import { ISchedule, IScheduleMany } from "../../types/schedule";
import { useAppDispatch } from "../../app/hooks";
import { selectUser } from "../../features/auth/authSlice";
import { selectRegistrant, setActiveProcesses, setProcess, setRegistrant } from "../../features/registrants/registrantsSlice";
import { addComment } from "../../features/comments/commentSlice";
import { showErrorSnackbar, showSuccessSnackbar } from "../../features/snackbar/snackbarSlice";
import AppointmentCalendar from "./process/AppointmentCalendar";
import { CREATEAPPOINTMENT, GETSCHEDULES } from "./process/BookAnAppointmentDialog";
import { convertAllDates } from "../../utils/function";

export const PRIMARY_OBJECTIONS = [
  "Not Specified",
  "Pricing",
  "Location",
  "Market Conditions",
  "Timing / Busy",
  "Already working on other projects",
  "Not interested in project",
  "Unable to attend event",
  "No Response / Voicemail",
  "Other",
];

// Shown while calling a registrant. Connected and Save logs a connection with the primary objection, plus a note
// and an appointment when those were filled in. Not Connected just closes.
const CallOutcomeDialog = (props: ChildProps) => {
  const { open, registrant, onClose } = props;
  const storeDispatch = useAppDispatch();
  const user = useSelector(selectUser);
  const storeRegistrant = useSelector(selectRegistrant);
  const [primaryObjection, setPrimaryObjection] = useState<string>(PRIMARY_OBJECTIONS[0]);
  const [note, setNote] = useState<string>("");
  // Appointment (same event and time slot fields as Book an Appointment). Only booked when a time slot was picked
  const [schedules, setSchedules] = useState<ISchedule[]>([]);
  const [schedule, setSchedule] = useState<ISchedule | null>(null);
  const [date, setDate] = useState<Date | null | undefined>(null);
  const [location, setLocation] = useState<string>("");
  const [noEmail, setNoEmail] = useState<boolean>(false);

  // Same query and filter as Book an Appointment
  const [getSchedules] = useLazyQuery<IScheduleMany>(GETSCHEDULES, {
    fetchPolicy: "cache-first",
    variables: {
      filter: { projects: user?.projectAccess.map((projectAccess: IProjectAccess) => projectAccess.project._id), active: true, upcoming: true },
    },
    onCompleted: (data) => setSchedules([...data.scheduleMany].sort((a: any, b: any) => b._id.localeCompare(a._id))),
  });

  useEffect(() => {
    if (open) getSchedules();
  }, [open, getSchedules]);

  const [createComment] = useMutation(CREATECOMMENT);
  const [createAppointment] = useMutation(CREATEAPPOINTMENT);
  const [addConnection] = useMutation(ADDREGISTRANTCONNECTION);
  // Covers all three steps so the buttons stay disabled between them
  const [saving, setSaving] = useState<boolean>(false);
  // Note / appointment already created in this call, so retrying after a later step fails does not create them again
  const created = useRef<{ comment: any; booked: any }>({ comment: null, booked: null });

  const registrantName = `${registrant.firstName || ""} ${registrant.lastName || ""}`.trim() || registrant.primaryPhone || "this registrant";

  // A time slot belongs to its event, so changing the event clears the chosen time
  const handleSchedule = (next: ISchedule | null) => {
    if ((next?._id || null) !== (schedule?._id || null)) setDate(null);
    setSchedule(next);
  };

  const handleClose = () => {
    setPrimaryObjection(PRIMARY_OBJECTIONS[0]);
    setNote("");
    setSchedule(null);
    setDate(null);
    setLocation("");
    setNoEmail(false);
    created.current = { comment: null, booked: null };
    onClose();
  };

  const handleSave = async () => {
    if (saving) return;
    if (date && !registrant.email) return storeDispatch(showErrorSnackbar("Registrant needs an email to book an appointment"));

    setSaving(true);
    try {
      // The note is created the same way as on the Notes tab, then linked to the registrant with the connection
      let comment = created.current.comment;
      if (!comment && note.trim()) {
        const { data } = await createComment({
          variables: {
            record: {
              user: user?._id,
              project: registrant.project._id,
              type: "general",
              comment: note.trim(),
              registrant: registrant._id,
              step: null,
              pinned: false,
              deleted: false,
            },
          },
        });
        comment = data.commentCreateOne.record;
        created.current.comment = comment;
      }

      // Booked with the same mutation and values as Book an Appointment, using the registrant's details
      let booked = created.current.booked;
      if (!booked && date) {
        const { data } = await createAppointment({
          variables: {
            projectId: schedule ? schedule.project._id : registrant.project._id,
            userId: user?._id,
            schedule: schedule ? schedule._id : null,
            submitDate: new Date(date),
            submitLocation: location || schedule?.locations?.[0] || registrant.project.salesOffice || "Sales Office",
            purchaserInfo: {
              firstName: registrant.firstName,
              lastName: registrant.lastName,
              email: registrant.email,
              primaryPhone: registrant.primaryPhone,
              numberOfGuests: 0,
              _id: registrant._id,
            },
            questions: [],
            status: "Pending",
            type: "lead",
            confirmed: false,
            cancelled: false,
            salesRepId: null,
            virtual: false,
            noEmail,
          },
        });
        booked = data.appointmentCreateOne;
        created.current.booked = booked;
      }

      const { data } = await addConnection({
        variables: { registrant: registrant._id, primaryObjection, comment: comment?._id || null, appointment: booked?.appointment?._id || null },
      });

      const current = storeRegistrant && storeRegistrant._id === registrant._id ? storeRegistrant : registrant;
      storeDispatch(
        setRegistrant({
          ...current,
          connections: data.addRegistrantConnection.connections,
          ...(booked
            ? { process: [...(current.process || []), ...booked.process], appointments: [...(current.appointments || []), booked.appointment] }
            : {}),
        })
      );
      if (booked) {
        storeDispatch(setActiveProcesses([...booked.process]));
        storeDispatch(setProcess(booked.process));
      }
      if (comment) storeDispatch(addComment(comment));
      storeDispatch(showSuccessSnackbar(["Connection", ...(comment ? ["note"] : []), ...(booked ? ["appointment"] : [])].join(", ") + " saved"));
      setSaving(false);
      handleClose();
    } catch (err) {
      setSaving(false);
      const saved = [...(created.current.comment ? ["note"] : []), ...(created.current.booked ? ["appointment"] : [])];
      storeDispatch(showErrorSnackbar(`Error saving call${saved.length ? ` (${saved.join(" and ")} already saved, Connected and Save will not create them again)` : ""}`));
    }
  };

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle>
        <strong>Current Call</strong>
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 3 }}>
          You are currently calling <strong>{registrantName}</strong>.
        </Typography>
        <FormControl fullWidth>
          <InputLabel id="primary-objection-label">Primary Objection</InputLabel>
          <Select
            labelId="primary-objection-label"
            label="Primary Objection"
            value={primaryObjection}
            onChange={(e) => setPrimaryObjection(e.target.value)}
          >
            {PRIMARY_OBJECTIONS.map((objection) => (
              <MenuItem key={objection} value={objection}>
                {objection}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Note"
          placeholder="Add a note about this call (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          multiline
          minRows={3}
          fullWidth
          sx={{ mt: 2 }}
        />
        <Typography sx={{ mt: 3, mb: 1 }}>
          <strong>Book an Appointment</strong> (optional)
        </Typography>
        <AppointmentCalendar
          user={undefined}
          setDate={setDate}
          setFormError={() => {}}
          schedules={schedules}
          schedule={schedule}
          setSchedule={handleSchedule}
          project={registrant.project}
          setLocation={setLocation}
          hideCalendar
          hideTimePicker
        />
        {schedule ? (
          <Typography variant="body2" sx={{ mt: 1, color: date ? "success.main" : "text.secondary" }}>
            {date ? `Appointment will be booked for ${convertAllDates(date, "PPpp")}` : "Pick a time slot to book an appointment"}
          </Typography>
        ) : null}
        <Box>
          <FormControlLabel
            control={<Checkbox checked={noEmail} onChange={() => setNoEmail(!noEmail)} />}
            label="Do not send Confirmation Email"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" color="error" disabled={saving} onClick={handleClose}>
          Not Connected
        </Button>
        <Button variant="contained" color="primary" disabled={saving} onClick={handleSave}>
          {saving ? <CircularProgress size={20} color="inherit" /> : "Connected and Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface ChildProps {
  open: boolean;
  registrant: IRegistrant;
  onClose: () => void;
}

// Same fields as the Notes tab so the new note can be added to its list
const CREATECOMMENT = gql`
  mutation commentCreateOne($record: CreateOneCommentInput!) {
    commentCreateOne(record: $record) {
      record {
        _id
        comment
        registrant {
          _id
        }
        step {
          _id
        }
        pinned
        user {
          _id
          fullName
        }
        type
        deleted
        createdAt
        updatedAt
      }
    }
  }
`;

const ADDREGISTRANTCONNECTION = gql`
  mutation addRegistrantConnection($registrant: MongoID!, $primaryObjection: String, $comment: MongoID, $appointment: MongoID) {
    addRegistrantConnection(registrant: $registrant, primaryObjection: $primaryObjection, comment: $comment, appointment: $appointment) {
      _id
      connections {
        date
        primaryObjection
        user {
          _id
          fullName
        }
      }
    }
  }
`;

export default CallOutcomeDialog;
